/**
 * The game genres, for the client: the genre picker in the Arche Builder and
 * Fias Forge, the Store's genre chips, and the Games page.
 *
 * A copy of `GAME_GENRES` in `@fias/db-types` (the client does not depend on
 * that package). The server is the authority — it refuses an unknown genre —
 * so drift would never be a hole, but it would be a lie: a picker offering a
 * genre the publish refuses, or hiding one it accepts. Pinned by
 * `tests/architecture/game-genres-client-parity.test.ts`. Ids are permanent.
 */

export interface GameGenreOption {
  id: string;
  label: string;
  /** The genre this one is a kind of: its games are listed there too. */
  parent?: string;
}

export const GAME_GENRE_OPTIONS: readonly GameGenreOption[] = [
  { id: 'action', label: 'Action & Arcade' },
  { id: 'rpg', label: 'Adventure & RPG' },
  { id: 'board', label: 'Board Games' },
  { id: 'cards', label: 'Cards & Solitaire' },
  { id: 'casual', label: 'Casual' },
  { id: 'crafting', label: 'Crafting & Building' },
  { id: 'flying', label: 'Flying' },
  { id: 'match', label: 'Match & Merge' },
  { id: 'platform', label: 'Platform & Runner' },
  { id: 'puzzle', label: 'Puzzle & Logic' },
  { id: 'strategy', label: 'Strategy' },
  { id: 'word', label: 'Word' },
  { id: 'racing', label: 'Racing' },
  { id: 'sports', label: 'Sports' },
  { id: 'simulation', label: 'Simulation' },
  { id: 'trivia', label: 'Trivia' },
  { id: 'music', label: 'Music' },
  { id: 'educational', label: 'Educational' },
  { id: 'cozy', label: 'Cozy' },
  { id: 'idle', label: 'Idle' },
  { id: 'tower-defense', label: 'Tower Defense', parent: 'strategy' },
];

/**
 * A game names at least one genre and at most this many — unless an admin
 * allowed its arche more (the server says how many: `gameGenreLimit`).
 */
export const GAME_MAX_GENRES = 4;

const LABEL_BY_ID = new Map(GAME_GENRE_OPTIONS.map((g) => [g.id, g.label]));

/** The label for a genre id, or the id itself for one this copy doesn't know. */
export function gameGenreLabel(id: string): string {
  return LABEL_BY_ID.get(id) ?? id;
}

/**
 * The selection after toggling one genre: on goes to the end, off removes it.
 * A toggle that would pass the limit is ignored rather than dropping another.
 */
export function toggleGameGenre(
  selected: readonly string[],
  id: string,
  max: number = GAME_MAX_GENRES,
): string[] {
  if (selected.includes(id)) return selected.filter((g) => g !== id);
  if (selected.length >= max) return [...selected];
  return [...selected, id];
}
